import axios from "axios";
import { IOccurrence } from "interfaces/Occurrence";
import { useCallback, useEffect, useState } from "react";
import { ReactComponent as MarkerIcon } from "assets/marker.svg";
import { ReactComponent as Timer } from "assets/timer2.svg";
import { ReactComponent as BookIcon } from "assets/product.svg";
import { ReactComponent as Copy } from "assets/copy-one.svg";

import { IAttachment } from "interfaces/Attachments";
import SharePictureModal from "../SharePictureModal";
import * as S from "./styles";
import { formatDate } from "utils/formatters";
import { IReport } from "interfaces/Report";

const GOOGLE_API_KEY = "AIzaSyCyYIwYpE42g7zN-nVRrPKkAwRseyccpaM";

type OccurrenceInfoProps = {
  occurrence: IOccurrence;
  onTrackButtonClick: () => void;
  isTrackButtonDisabled: boolean;
  selectedReport: IReport | null
};

export default function OccurrenceInfo({
  occurrence,
  selectedReport,
  onTrackButtonClick,
  isTrackButtonDisabled,
}: OccurrenceInfoProps) {
  const [lastSeenWhereAddress, setLastSeenWhereAddress] = useState<string>("");
  const [modalImg, setModalImg] = useState<IAttachment>();
  const [isPictureModalOpen, setIsPictureModalOpen] = useState<boolean>(false);

  function formatAddress() {
    const { street, number, city, uf } =
      occurrence.occurrencesSuperficialAddresses.superficialAddress;

    return `${street}, ${number ?? ''} - ${city} - ${uf}`;
  }

  function handleCopyAddress(value: string) {
    navigator.clipboard.writeText(value);
  }

  function handleImageClick(attachment: IAttachment) {
    setModalImg(attachment);
    setIsPictureModalOpen(true);
  }

  const getLastSeenLocation = useCallback(async () => {
    const lastSeenCoordX = occurrence.lastCoordX;
    const lastSeenCoordY = occurrence.lastCoordY;

    axios
      .get(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lastSeenCoordX},${lastSeenCoordY}&key=${GOOGLE_API_KEY}`
      )
      .then(({ data }) => {
        const address = data?.results?.[0]?.formatted_address;

        const [street, numberAndDistrict, city, cep] = address
          .split(",")
          .map((item: string) => item.trim());

        setLastSeenWhereAddress(
          `${street}, ${numberAndDistrict}, ${city}, ${cep}`
        );
      });
  }, [occurrence]);

  const estimatedArrival = new Date(
    selectedReport?.estimatedArrival as unknown as string
  );

  const arrivalTime = selectedReport?.arrivalTime
    ? new Date(selectedReport?.arrivalTime as unknown as string)
    : null;

  useEffect(() => {
    getLastSeenLocation();
  }, [occurrence, getLastSeenLocation]);

  return (
    <S.Wrapper data-cy="occurrence-info">
      <S.Section>
        <S.SectionHeader>
          <MarkerIcon width={20} height={20} />
          <h2>Endereço</h2>
        </S.SectionHeader>

        <S.AddressesWrapper>
          <S.AddressRow>
            <S.Topic>
              <S.Subtitle>Endereço de origem</S.Subtitle>
              <p>{formatAddress()}</p>
            </S.Topic>
            <Copy
              title="Copiar endereço"
              data-cy="occurrence-info-copy-address"
              onClick={() => handleCopyAddress(formatAddress())}
            />
          </S.AddressRow>

          {occurrence.patrimonialType === 2 && (
            <S.Topic>
              <S.AddressRow>
                <div>
                  <S.Subtitle>Ultima vez visto em</S.Subtitle>
                  <p>{lastSeenWhereAddress}</p>
                </div>
                <Copy
                  title="Copiar endereço"
                  data-cy="occurrence-info-copy-last-seen-where-address"
                  onClick={() => handleCopyAddress(lastSeenWhereAddress)}
                />
              </S.AddressRow>
            </S.Topic>
          )}

          <S.AddressRow>
            <S.TrackButton
              onClick={onTrackButtonClick}
              disabled={isTrackButtonDisabled}
              title={
                isTrackButtonDisabled
                  ? "O apoio ainda nao enviou nenhuma coordenada, portanto nao sera possivel visualizar o trajeto."
                  : ""
              }
            >
              Visualizar trajeto do agente
            </S.TrackButton>
          </S.AddressRow>
        </S.AddressesWrapper>
      </S.Section>

      <S.Section>
        <S.SectionHeader>
          <Timer fill="#FF8F01" width={20} height={20} />
          <h2>Tempos </h2>
        </S.SectionHeader>

        <S.ArrivalTimeWrapper>
          <S.Topic>
            <S.Subtitle>Estimativa de tempo de chegada</S.Subtitle>
            {selectedReport?.estimatedArrival ? (
              <S.Badge>
                <span>
                  Chegada estimada para {formatDate(estimatedArrival)}
                </span>
              </S.Badge>
            ) : (
              <p>Nenhuma estimativa de chegada foi sinalizada!</p>
            )}
          </S.Topic>
          <S.Topic>
            <S.Subtitle>Tempo de chegada</S.Subtitle>
            <p>
              {arrivalTime ? (
                <S.Badge>
                  <span>
                    A chegada do pronta-resposta foi registrada às{" "}
                    {formatDate(arrivalTime)}
                  </span>
                </S.Badge>
              ) : (
                <p>Agente ainda não chegou no local da ocorrência!</p>
              )}
            </p>
          </S.Topic>
          <S.Topic>
            <S.Subtitle>Tempo total da ocorrencia</S.Subtitle>
            <p>
              {selectedReport?.totalTime ??
                "Erro ao calcular tempo total!"}
            </p>
          </S.Topic>
        </S.ArrivalTimeWrapper>
      </S.Section>

      <S.Section>
        <S.SectionHeader>
          <BookIcon />
          <h2>Cliente</h2>
        </S.SectionHeader>

        <S.ClientInfoWrapper>
          <S.Topic>
            <S.Subtitle>Nome fantasia</S.Subtitle>
            <p>{occurrence.client.tradingName}</p>
          </S.Topic>
          <S.Topic>
            <S.Subtitle>Razão social</S.Subtitle>
            <p>{occurrence.client.name}</p>
          </S.Topic>
          <S.Topic>
            <S.Subtitle>CNPJ</S.Subtitle>
            <p>{occurrence.client.cnpj}</p>
          </S.Topic>
          <S.Topic>
            <S.Subtitle>E-mail</S.Subtitle>
            <p>{occurrence.client.email}</p>
          </S.Topic>
          <S.Topic>
            <S.Subtitle>Nome do proprietário</S.Subtitle>
            <p>{occurrence.client.managerName}</p>
          </S.Topic>
          <S.Topic>
            <S.Subtitle>Beneficiário</S.Subtitle>
            <p>
              {
                occurrence.occurrencesSuperficialAddresses.superficialAddress
                  ?.superficialClient?.name
              }
            </p>
          </S.Topic>
        </S.ClientInfoWrapper>
      </S.Section>

      {selectedReport?.attachments &&
        selectedReport.attachments.length > 0 && (
          <S.Section>
            <S.SectionHeader>
              <h2>Anexos</h2>
            </S.SectionHeader>

            <S.AttachmentsWrapper>
              {selectedReport &&
                selectedReport.attachments?.map((attachment) => (
                  <img
                    key={attachment.url}
                    src={attachment.url}
                    alt={attachment.caption}
                    onClick={() => handleImageClick(attachment)}
                  />
                ))}
            </S.AttachmentsWrapper>
          </S.Section>
        )}

      {!!modalImg && (
        <SharePictureModal
          imgUrl={modalImg.url}
          imgAlt={modalImg?.caption ?? "Modal image"}
          coord={modalImg?.coord}
          isOpen={isPictureModalOpen}
          onCloseButtonClick={() => {
            setIsPictureModalOpen(false);
            setModalImg(undefined);
          }}
        />
      )}
    </S.Wrapper>
  );
}
